import { useEffect, useState } from "react";
import { FloppyDisk, Info } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import NotificationPrefsEditor, { mergeNotifPrefs } from "@/components/NotificationPrefsEditor";

export default function NotificationPrefs() {
  const { user } = useAuth();
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(false);
  const isOwner = user?.role === "owner";

  const load = () => {
    setForm(null); setErr(false);
    api.get("/me/notifications")
      .then((r) => setForm(mergeNotifPrefs(r.data)))
      .catch(() => setErr(true));
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setBusy(true);
    try { await api.put(`/team/${user.id}/notifications`, form); toast.success("Notification preferences saved"); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  if (err) return <div className="space-y-3" data-testid="np-load-error"><p className="text-sm text-destructive">Couldn't load your notification preferences.</p><Button variant="outline" className="rounded-full" onClick={load}>Retry</Button></div>;
  if (!form) return <div className="text-muted-foreground font-mono text-sm animate-pulse">Loading preferences…</div>;

  return (
    <div className="space-y-6 max-w-2xl" data-testid="notification-prefs-page">
      <PageHeader overline="My Account" title="Notification Preferences" subtitle="What reaches you, and how" />

      {!isOwner && (
        <div className="flex items-start gap-2 rounded-lg bg-sky-50 border border-sky-200 px-3 py-2.5" data-testid="np-readonly-note">
          <Info size={16} className="text-sky-700 mt-0.5 shrink-0" />
          <p className="text-xs text-sky-800">These preferences are managed by your firm owner. You can see what's set for you here; ask the owner to make any changes.</p>
        </div>
      )}

      <NotificationPrefsEditor value={form} onChange={setForm} role={user?.role} readOnly={!isOwner} />

      {isOwner && (
        <Button onClick={save} disabled={busy} className="rounded-full bg-primary hover:bg-[#113A64]" data-testid="save-notifications-btn">
          <FloppyDisk size={16} className="mr-1" /> {busy ? "Saving…" : "Save Preferences"}
        </Button>
      )}
    </div>
  );
}
